import axiosRetry from 'axios-retry'
import axios from 'axios'
import { defineNuxtPlugin, type NuxtApp } from 'nuxt/app'
import * as saasApi from '@sec-auditor/saas-api-sdk'
import * as apiSdk from '~/dependencies/api-sdk/src'
import type { FetchAPI, Middleware } from '~/dependencies/api-sdk/src'

const createFetchWithRetry = (baseFetch: FetchAPI = fetch): FetchAPI => {
  return async (url: string, init: RequestInit): Promise<Response> => {
    const maxRetries = 3
    let retries = 0
    let lastError: unknown

    while (retries < maxRetries) {
      try {
        const response = await baseFetch(url, init)

        // If we get a 5xx response, treat it as retryable
        if (response.status >= 500 && response.status < 600) {
          lastError = response
          retries++

          // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, retries) * 200))
          continue
        }

        // Return successful response or non-5xx errors
        return response
      } catch (error) {
        lastError = error
        retries++

        // Only retry on network errors
        if (!(error instanceof TypeError)) {
          throw error
        }

        // Exponential backoff for network errors
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, retries) * 200))
      }
    }

    // If we've exhausted retries, throw the last error we encountered
    if (lastError instanceof Response) {
      return lastError // Return the last error response
    }

    throw lastError // Throw the last error if it wasn't a Response object
  }
}

export default defineNuxtPlugin((nuxtApp: NuxtApp) => {
  try {
    const tokenHelper = useAuthToken()
    const envVars = useRuntimeConfig()

    // shared sign-out path for genuinely dead sessions (refresh token expired/revoked)
    const handleDeadSession = (): void => {
      tokenHelper.handleDeadSession()
    }

    // axios default setup, token is resolved per request from the live session
    const axiosInstance = axios.create()

    axiosInstance.defaults.withCredentials = true

    axiosInstance.interceptors.request.use(async (config) => {
      const token = await tokenHelper.getAccessToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    // retry on 5xx response codes
    axiosRetry(axiosInstance, {
      retries: 3,
      retryDelay: axiosRetry.exponentialDelay
    })

    // refresh session and retry once if backend returns 401
    axiosInstance.interceptors.response.use(null, async (error) => {
      if (
        error.config &&
        error.response &&
        error.response.status === 401 &&
        !error.config._authRetried
      ) {
        error.config._authRetried = true

        const freshSession = await tokenHelper.getFreshSession({ force: true })
        if (freshSession?.accessToken) {
          error.config.headers.Authorization = `Bearer ${freshSession.accessToken}`
          return axiosInstance.request(error.config)
        }

        handleDeadSession()
      }

      // handle error in axios-retry
      return Promise.reject(error)
    })

    const fetchWithRetry = createFetchWithRetry()

    // refresh session and retry once on 401, only fires on real session loss
    const auth401Middleware: Middleware = {
      post: async (context): Promise<Response | undefined> => {
        if (context.response.status !== 401) {
          return
        }

        const freshSession = await tokenHelper.getFreshSession({ force: true })
        if (!freshSession?.accessToken) {
          // dead session: sign out and let the 401 propagate
          handleDeadSession()
          return
        }

        // context.fetch would recurse into this middleware, so retry directly
        return await fetchWithRetry(context.url, {
          ...context.init,
          headers: {
            ...context.init.headers as Record<string, string>,
            Authorization: `Bearer ${freshSession.accessToken}`
          }
        })
      }
    }

    const configuration = new apiSdk.Configuration({
      basePath: envVars.public.API_URL,
      accessToken: async (): Promise<string> => (await tokenHelper.getAccessToken()) ?? '',
      middleware: [auth401Middleware],
      fetchApi: fetchWithRetry
    })

    const client = new apiSdk.ClientApi(configuration)
    const serviceprovider = new apiSdk.ServiceproviderApi(configuration)
    const other = new apiSdk.OtherApi(configuration)
    const user = new apiSdk.UserApi(configuration)
    const admin = new apiSdk.AdminApi(configuration)
    const integrations = new apiSdk.IntegrationsApi(configuration)

    const saas = {
      billing: new saasApi.BillingApi(undefined, envVars.public.SAAS_API_URL, axiosInstance),
      other: new saasApi.OtherApi(undefined, envVars.public.SAAS_API_URL, axiosInstance),
      user: new saasApi.UserApi(undefined, envVars.public.SAAS_API_URL, axiosInstance)
    }

    // Inject to context as $api
    const api = { client, serviceprovider, other, user, admin, integrations }
    return {
      provide: {
        api,
        saas,
        axiosInstance
      }
    }
  } catch (e) {
    nuxtApp.$logger.error(e)
  }
})
